import { Injectable } from '@angular/core';
import { ReplaySubject, Observable } from 'rxjs';
import { initialize, LDClient, LDFlagSet } from 'launchdarkly-js-client-sdk';
import { environment } from '@env/environment';
import { AuthenticationService, User } from '@app/auth';
import { map } from 'rxjs/operators';
import { datadogRum } from '@datadog/browser-rum';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {

  private flagChange: ReplaySubject<any> = new ReplaySubject<any>(1);
  private _ldClient: LDClient;

  constructor(private authService: AuthenticationService) {
    this._ldClient = this.initializeLdClient();
    this.flagChange.next({});
    this.authService.user$.subscribe((user) => {
      this.identifyUser(user);
    });
    this._ldClient.on('ready', () => {
      this.updateFlags();
    });
    this._ldClient.on('change', () => {
      this.updateFlags();
    });
    this._ldClient.on('failed', () => {
      const error: Error = new Error('Unable to connect to LaunchDarkly')
      datadogRum.addError(error);
    });
  }

  /**
   * Gets a single flag if the flag is not present it returns false
   * @param flagName name of the flag to be retrieved
   * @returns observable that emits the value of the flag
   */
  getFlag(flagName: string): Observable<boolean> {
    return this.flagChange.asObservable().pipe(map((flags) => flags[flagName] ?? false));
  }

  /**
   * Gets the map of flags asynchronously
   * @returns observable that emits the map of flags
   */
  getFlags(): Observable<LDFlagSet> {
    return this.flagChange.asObservable();
  }

  /**
   * Identifies the user to featureflag and sets up the first flags,
   * then keeps listening for changes.
   * @returns observable which fails if user could not be identified
   */
  identifyUser(user: User) {
    if (user) {
      this._ldClient.identify({
        key: btoa(user.username),
        email: user.email,
        name: user.username,
        anonymous: false,
      });
    }
  }

  /**
   * Updates the flag map with new flag values
   */
  private updateFlags() {
    this.flagChange.next(this._ldClient.allFlags());
  }

  /**
   * Initializes the ld client with the required settings
   * @returns the instance of the LDClient created
   */
  private initializeLdClient(): LDClient {
    return initialize(environment.featureFlagClientId, {
      kind: 'user',
      key: environment.featureFlagSDKKey,
      anonymous: true,
    },
    {
      streamUrl: environment.relayProxyBaseUrl,
      baseUrl: environment.relayProxyBaseUrl,
      eventsUrl: environment.relayProxyBaseUrl,
    });
  }

}

export const ffKeys = {
  enableGeneralIncentiveModal: 'enable-general-incentive-modal',
  icKnowledgeChannels: [
    'a6pDo000000wtBFIAY',
    'a6pDo000000wtB5IAI',
    'a6pDo000000wtBAIAY',
    'a6pDo000000wtB0IAI',
    'a6pDo000000wtBKIAY',
    'a6pDo000000wtBPIAY',
    'a6p4y000000wqHLAAY',
    'a6p4y000000wqHKAAY',
    'a6p4y000000wqHIAAY',
    'a6p4y000000wqH6AAI',
    'a6p4y000000wqHPAAY',
    'a6p4y000000wqHQAAY',
    'a6pDo000000bs17IAA',
    'a6p4y000000wqH0AAI',
    'a6p4y000000wqGrAAI',
    'a6p4y000000wqGqAAI',
    'a6pDo000000534fIAA',
    'a6p4y000000L9YcAAK',
    'a6p4y000000wqHAAAY',
    'a6p4y000000wqHGAAY',
    'a6p4y000000wqHUAAY'
  ],
  enableHGEmbeddedImageViewer: 'cp-22435-hygraph-embedded-image-viewer',
  enableSocialUsersPermanent: 'cp-13296-social-email-accounts-block-access',
  enableSharingHygraphAssets: 'cp-21225-sharing-hygraph-assets',
  spotlightHomePage: 'cp-22181-home-page-spotlight-component',
  enableHygraphBlogSupport: 'cp-21112-hygraph-blog-support-in-uui',
  mobileAppPromotionBanner: 'mobile-app-promotion-banner',
  enableDownloadAndShareOptionsForHygraphHTMLViewer: 'download-and-share-options-on-html-viewer-cp-22550',
  enableAltPdfViewer: 'cp-23611-display-pd-fs-without-pdf-tron',
  enableInsightfulChatHome: 'enable-insightful-chat-home-page',
  enableCVOOptimizeView: 'CP-24150-optimize-cvo-display-viewer',
  infiniteScrollViewAll: 'cp-23228-infinite-scroll-on-category-landing-pages',
  publishDateFilter: 'cp-25129-search-filter-for-from-and-to-dates',
  enablePdfSearch: 'cp-25444-add-search-to-pdf-viewer',
  enableLandingPageTabs: 'cp-22467-landing-page-tabs',
  enableAddToFavoritesHygraph: 'cp-23595-add-hygraph-reports-to-favorites',
  enableImageResolutionToggle: 'cp-26866-high-resolution-image-option-in-image-viewer',
  enableSubSearchTerms: 'fe-cp-24782-search-within-results-on-global-search',
  enableHygraphInsightfulChat: 'cp-27132-ask-insightful-chat-question-from-hygraph-pages',
  enableMySubscriptionPageRedesign: 'cp-26838-my-subscription-page-redesign',
  enableAdditionalLinksSubscriptionItems: 'cp-28562-my-subscription-page-additional-links-to-subscription-items',
  hideLegacyDatabaseTools: 'cp-28370-remove-legacy-bom-db-cards',
  enableTrendingContentByPersona: 'cp-27947-narrow-trending-content-by-persona',
  enableImageAnnotationTool: 'platform-image-annotation-tool',
  enablePlatformNotifications: 'CP-28655-FE-platform-notifications-via-sendgrid',
  dpcmRedirectionFlag: 'cp-20128-discrete-and-power-cost-sme-model',
  enableCoverageTag: 'cp-27881-add-coverage-area-tag-as-a-filter',
}
